import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import {
  DokumentEntwurf,
  ErzeugendesModul,
  ProjektDokument,
} from '../components/dokumente/types';
import {
  erzeugteLoeschSperre,
  inhaltstyp,
  istErzeugteUnterlage,
  normalisiereOrdner,
  storagePfad,
} from '../components/dokumente/dokumenteLogic';

/**
 * Datenbank- und Storage-Zugriff des Dokumentenmanagements (Tabelle
 * `project_documents` + Bucket `projekt-dokumente`, supabase/
 * project_documents.sql, MIGRATIONS.md Nr. 37) — die einzige Stelle, die mit
 * ihnen redet.
 *
 * Die REGELN liegen daneben in `src/components/dokumente/dokumenteLogic.ts`
 * und sind ohne Datenbank pruefbar; hier steht nur, was ohne Supabase nicht zu
 * haben ist.
 *
 * ═══ DREI ENTSCHEIDUNGEN, DIE HIER FESTGENAGELT SIND ═════════════════════
 *
 * (a) ERST DIE DATEI, DANN DIE ZEILE — UND BEIM LOESCHEN GENAUSO.
 *     Beim Anlegen wird zuerst hochgeladen und dann die Zeile geschrieben;
 *     scheitert die Zeile, wird die Datei wieder abgeraeumt (`remove`), sonst
 *     bliebe ein bezahltes Byte ohne Verweis liegen. Ob der Rueckbau gelungen
 *     ist, steht am geworfenen Fehler (`AblageFehler.rueckbau`) — ein
 *     Aufrufer, der den Zeilenfehler als harmlos behandelt (Doppelablage),
 *     darf eine liegengebliebene Datei nicht als Erfolg melden.
 *     Beim Loeschen ist die Reihenfolge dieselbe: erst die Datei, dann die
 *     Zeile. Das ist die UMGEKEHRTE Reihenfolge zu `deleteTenderDocument`, und
 *     zwar mit Absicht — dieses Modul existiert, um Speicher freizugeben.
 *     Faellt der zweite Schritt aus, bleibt eine Zeile ohne Datei stehen: das
 *     sieht man, und es laesst sich erneut loeschen. Andersherum bliebe eine
 *     Datei ohne Zeile liegen: die sieht niemand mehr, und der Speicher, den
 *     das Archivieren sparen soll, bleibt belegt.
 *
 * (b) EINE HOCHGELADENE DATEI WIRD NIE UEBERSCHRIEBEN.
 *     `upsert: false` auf einen frischen UUID-Pfad; der Bucket hat gar keine
 *     update-Policy. Eine neue Fassung eines Plans ist ein neues Dokument mit
 *     eigenem Stand. Wer das je aufweicht, nimmt der Ablage ihre
 *     Nachvollziehbarkeit.
 *
 * (c) EINE FEHLENDE MIGRATION REISST DIE ANSICHT NICHT AUF.
 *     `fetchProjektDokumente` faengt `42P01`/`PGRST205` ab und liefert eine
 *     leere Liste — dieselbe Zusicherung wie `fetchEinweisungen` und
 *     `fetchInvoiceCostCenters`. Solange Nr. 37 nicht gefahren ist, gibt es
 *     eben keine Ablage; das Projekt bleibt bedienbar. SCHREIBENDE Aufrufe
 *     melden den Fehler dagegen sichtbar — ein stiller Upload ins Nichts waere
 *     schlimmer als eine Fehlermeldung.
 */

const TABELLE = 'project_documents';

/** Privater Bucket der Projektablage. */
const BUCKET = 'projekt-dokumente';

/** Gueltigkeit einer signierten Download-URL. Eine Stunde reicht zum Ansehen. */
const SIGNED_URL_SEKUNDEN = 3600;

/**
 * Wie viele Dateien je Aufruf an `remove()` gehen. Supabase nimmt Listen
 * entgegen, aber eine Ablage mit 800 Fotos in EINEM Aufruf laeuft in die
 * Anfragegrenze — und ein Teilerfolg waere dann nicht erkennbar.
 */
const LOESCH_STAPEL = 100;

/**
 * Seitengroesse beim Lesen.
 *
 * PostgREST deckelt Antworten serverseitig (in Supabase-Projekten ueblicherweise
 * bei 1000 Zeilen), OHNE das zu melden — eine ungepagte Abfrage liefert dann
 * stillschweigend einen Ausschnitt. Genau das waere hier gefaehrlich: das
 * Raeumen beim Archivieren loescht die Dateien der geholten Liste. Bekaeme es
 * nur die ersten 1000 von 1500, blieben 500 Dateien ohne Zeile liegen —
 * unsichtbar und dauerhaft bezahlt.
 */
const SEITE = 1000;

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/** Was aus der Datei geworden ist, nachdem ihre Zeile gescheitert war. */
export interface RueckbauStand {
  /** Pfad im Bucket, unter dem die Datei vor dem Zeilenfehler hochgeladen wurde. */
  pfad: string;
  /**
   * `true` = die Datei liegt vermutlich noch da: das Entfernen wurde
   * abgewiesen, nicht bestaetigt oder ist selbst gescheitert. Sie belegt dann
   * Speicher ohne Verweis — unsichtbar, solange niemand davon erfaehrt.
   */
  verwaist: boolean;
}

/**
 * Ein Fehler der Tabelle, der seinen Postgres-/PostgREST-Code behaelt.
 *
 * `describeError` haengt den Code zwar an den Text, aber ein Aufrufer, der
 * auf GENAU einen Code reagieren muss, soll ihn nicht aus dem Satz
 * herausparsen: `legeUnterlageAb` behandelt 23505 (Unique-Index auf
 * `erzeugt_aus` + `fassung_ref`) als Wettlauf zweier gleichzeitiger Ablagen
 * und nicht als Fehler. Fuer alle anderen bleibt es ein gewoehnlicher Error
 * mit demselben Text wie bisher.
 *
 * `rueckbau` traegt bei einem Zeilenfehler nach dem Upload, ob die Datei
 * wieder weg ist. Genau der Aufrufer, der den Zeilenfehler als harmlos
 * einstuft, braucht diese Angabe — sonst meldet er Erfolg ueber einer
 * verwaisten Datei.
 */
export class AblageFehler extends Error {
  readonly code: string;
  readonly rueckbau: RueckbauStand | null;

  constructor(message: string, code: string, rueckbau: RueckbauStand | null = null) {
    super(message);
    this.name = 'AblageFehler';
    this.code = code;
    this.rueckbau = rueckbau;
  }
}

/** 23505 = unique_violation: dieselbe Fassung liegt schon in der Ablage. */
export const istDoppelablage = (e: unknown): boolean =>
  e instanceof AblageFehler && e.code === '23505';

/**
 * 42703 = undefined_column, PGRST204 = PostgREST kennt die Spalte nicht: die
 * Kennzeichnung erzeugter Unterlagen (Nr. 160/161) ist noch nicht gefahren.
 */
export const istFehlendeKennzeichnung = (e: unknown): boolean =>
  e instanceof AblageFehler && (e.code === '42703' || e.code === 'PGRST204');

/**
 * Die Tabelle gibt es noch nicht (Migration Nr. 37 offen).
 *
 * PGRST205 meldet PostgREST, 42P01 ist der Postgres-Code fuer
 * "undefined_table" — welcher ankommt, haengt daran, ob PostgREST das Schema
 * schon kennt. Muster wie `sigeEinweisungService.ts`.
 */
const istFehlendeTabelle = (e: unknown): boolean => {
  const code = errorCode(e);
  return code === 'PGRST205' || code === '42P01';
};

/**
 * Ohne Supabase-Konfiguration liefert `getSupabase()` ein handgeschriebenes
 * Ersatzobjekt, dessen `storage.from()` nur `upload`, `remove` und
 * `getPublicUrl` kennt. Ein Aufruf von `createSignedUrl` oder `download`
 * scheiterte dort mit „is not a function" statt mit der Konfigurationsmeldung,
 * die der Anwender braucht.
 */
const storageMitMethode = (methode: string) => {
  const eimer = supabase.storage.from(BUCKET) as Record<string, unknown>;
  if (typeof eimer[methode] !== 'function') {
    throw new Error(
      'Die Verbindung zur Ablage ist nicht eingerichtet (Supabase-Konfiguration fehlt).',
    );
  }
  return eimer as never;
};

/**
 * ═══ WARUM DIESE FUNKTION EXISTIERT: DER LEERE ROTE KASTEN ════════════════
 *
 * `download()` ruft Storage mit `noResolveJson: true` auf (es will einen Blob,
 * kein JSON). Faellt die Antwort negativ aus, landet in storage-js NICHT der
 * Fehlerkoerper im Fehler, sondern das rohe `Response`-Objekt — und dessen
 * Meldung wird mit `JSON.stringify(response)` gebildet. Ein `Response` hat
 * keine eigenen Felder, das ergibt woertlich `"{}"`.
 *
 * Genau das stand in der Oberflaeche: ein roter Kasten mit `{}`. Weder Status
 * noch Grund waren daran zu erkennen, und der eigentliche Text („Object not
 * found", „new row violates row-level security policy") lag ungelesen im
 * Koerper derselben Antwort.
 *
 * Diese Funktion liest ihn nach. Der Koerper ist zu diesem Zeitpunkt noch
 * ungelesen (storage-js hat ihn wegen `noResolveJson` nie angefasst), `text()`
 * ist also erlaubt und liefert genau eine Zeile Klartext.
 *
 * Sie ist bewusst `async` und wirft nie selbst: sie steht in einem Fehlerpfad,
 * und ein Fehler IM Fehlertext waere die schlechteste aller Meldungen.
 */
const antwortAusFehler = (e: unknown): Response | null => {
  const roh = (e as { originalError?: unknown } | null)?.originalError;
  const kandidat = roh && typeof roh === 'object' ? roh as Partial<Response> : null;
  return kandidat && typeof kandidat.status === 'number' && typeof kandidat.text === 'function'
    ? kandidat as Response
    : null;
};

/** Status aus beiden Fehlerarten: `StorageApiError.status` oder das rohe `Response`. */
const statusAusFehler = (e: unknown): number => {
  const api = (e as { status?: unknown } | null)?.status;
  if (typeof api === 'number') return api;
  return antwortAusFehler(e)?.status ?? 0;
};

/**
 * Klartext zu einem gescheiterten Speicher-Zugriff.
 *
 * `tat` steht im Satz („ansehen", „herunterladen"), damit der Anwender liest,
 * was NICHT geklappt hat, und nicht nur, dass etwas nicht geklappt hat.
 *
 * Die Statuszuordnung folgt dem, was Supabase Storage tatsaechlich schickt:
 * eine von der RLS abgewiesene Datei kommt als 400 oder 403 mit
 * `"Object not found"` im Koerper zurueck — Storage verraet bewusst nicht, ob
 * die Datei fehlt oder nur gesperrt ist. Beide Faelle bekommen deshalb
 * denselben, ehrlich unentschiedenen Satz statt einer erfundenen Diagnose.
 */
const speicherFehlertext = async (e: unknown, tat: string): Promise<string> => {
  const status = statusAusFehler(e);
  const antwort = antwortAusFehler(e);

  let koerper = '';
  if (antwort) {
    try {
      koerper = (await antwort.text()).slice(0, 400).trim();
    } catch {
      // Koerper schon gelesen oder Verbindung weg — der Status bleibt.
    }
  }

  const roh = describeError(e);
  // `"{}"` ist der oben beschriebene Platzhalter von storage-js und sagt
  // nichts; alles andere ist eine echte Meldung und darf mit.
  const gemeldet = roh && roh !== '{}' && roh !== 'StorageUnknownError' ? roh : '';
  const details = [koerper, gemeldet].filter(Boolean).join(' · ');
  const anhang = [status ? `HTTP ${status}` : '', details].filter(Boolean).join(' — ');

  if (status === 400 || status === 403 || status === 404) {
    return `Die Datei lässt sich nicht ${tat}: kein Zugriff oder nicht mehr vorhanden.\n`
      + 'Häufigster Grund: Für dieses Projekt fehlt die Berechtigung, oder das Dokument ist '
      + 'mit dem Admin-Schloss versehen. Bitte an die Verwaltung wenden.'
      + (anhang ? `\n(${anhang})` : '');
  }
  if (status === 401) {
    return `Die Anmeldung ist abgelaufen — bitte neu anmelden, dann lässt sich die Datei wieder ${tat}.`;
  }
  if (!status) {
    return `Die Datei ließ sich nicht ${tat} — die Verbindung zur Ablage kam nicht zustande.`
      + (details ? `\n(${details})` : '');
  }
  return `Die Datei ließ sich nicht ${tat}.` + (anhang ? `\n(${anhang})` : '');
};

/** Vorgaben fuer Felder, die die Datenbank NULL-bar haelt, der Client aber nie NULL sehen soll. */
const mapZeile = (row: Record<string, unknown>): ProjektDokument => ({
  ...(row as unknown as ProjektDokument),
  tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
  module: Array.isArray(row.module) ? (row.module as ProjektDokument['module']) : [],
  ordner: typeof row.ordner === 'string' ? row.ordner : '',
  groesse_bytes: typeof row.groesse_bytes === 'number' ? row.groesse_bytes : Number(row.groesse_bytes) || 0,
  // Solange Migration Nr. 38 nicht gefahren ist, gibt es die Spalte nicht.
  // `false` ist dann die richtige Antwort: ohne Schloss ist nichts gesperrt.
  //
  // ACHTUNG: Diese Nachsicht gilt NUR fuers Lesen. Schreibend ist das Modul
  // nicht tolerant — `uploadDokument` schickt `nur_admin` immer mit, und ohne
  // die Spalte antwortet PostgREST mit `PGRST204`/`42703`, jeder Upload
  // scheitert also sichtbar. Nr. 38 muss VOR dem Client-Deploy laufen.
  nur_admin: row.nur_admin === true,
});

// ─── Lesen ──────────────────────────────────────────────────────────────────

/**
 * Alle Dokumente eines Projekts, neueste zuerst.
 *
 * Wirft NICHT bei fehlender Tabelle (siehe (c) im Dateikopf).
 */
export const fetchProjektDokumente = async (projectId: string): Promise<ProjektDokument[]> => {
  if (!projectId) return [];
  const alle: ProjektDokument[] = [];
  for (let von = 0; ; von += SEITE) {
    const { data, error } = await supabase
      .from(TABELLE)
      .select('*')
      .eq('project_id', projectId)
      // Zweites Sortierkriterium: `created_at` allein ist bei gleichzeitig
      // hochgeladenen Zeilen nicht eindeutig, und eine uneindeutige Sortierung
      // laesst beim Blaettern Zeilen doppelt erscheinen oder ganz ausfallen.
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(von, von + SEITE - 1);
    if (error) {
      if (istFehlendeTabelle(error)) return [];
      throw new Error(describeError(error));
    }
    const seite = data || [];
    alle.push(...seite.map(mapZeile));
    if (seite.length < SEITE) return alle;
  }
};

/**
 * Die schon abgelegte Zeile zu einer Fassung — oder `null`.
 *
 * Der Vorab-Blick von `legeUnterlageAb`: eine Fassung, die schon liegt, wird
 * gar nicht erst hochgeladen. Der Unique-Index (Nr. 160/161) faengt den
 * Wettlauf zweier gleichzeitiger Ablagen; dieser Blick erspart der Datei den
 * Weg in den Bucket und wieder hinaus.
 *
 * `limit(1)` statt `maybeSingle()`: auf einer Umgebung ohne den Index koennte
 * eine Fassung zweimal liegen, und `maybeSingle()` wuerfe dann — die aelteste
 * Zeile ist in dem Fall die richtige Antwort. Wirft bei fehlender Spalte
 * (`istFehlendeKennzeichnung`), nicht bei fehlender Tabelle: ohne Tabelle
 * gibt es nichts, was schon abgelegt sein koennte.
 */
export const findeErzeugteUnterlage = async (
  projectId: string,
  modul: ErzeugendesModul,
  fassungRef: string,
): Promise<ProjektDokument | null> => {
  const ref = (fassungRef || '').trim();
  if (!projectId || !ref) return null;
  const { data, error } = await supabase
    .from(TABELLE)
    .select('*')
    .eq('project_id', projectId)
    .eq('erzeugt_aus', modul)
    .eq('fassung_ref', ref)
    .order('created_at', { ascending: true })
    .limit(1);
  if (error) {
    if (istFehlendeTabelle(error)) return null;
    throw new AblageFehler(describeError(error), errorCode(error));
  }
  const zeile = (data || [])[0] as Record<string, unknown> | undefined;
  return zeile ? mapZeile(zeile) : null;
};

// ─── Anlegen ────────────────────────────────────────────────────────────────

interface UploadArgs {
  projectId: string;
  datei: File | Blob;
  dateiname: string;
  entwurf: DokumentEntwurf;
  benutzerId?: string | null;
  benutzerName?: string | null;
}

/**
 * Laedt eine Datei in den Bucket und schreibt die zugehoerige Zeile.
 *
 * Reihenfolge und Rollback: siehe (a) im Dateikopf. Die Storage-Policy prueft
 * den Projektzugriff ueber den PFAD, nicht ueber die Zeile — deshalb darf die
 * Datei hier vor ihrer Zeile entstehen (anders als bei der Einweisung, wo der
 * Datensatz zuerst da sein muss).
 */
export const uploadDokument = async ({
  projectId, datei, dateiname, entwurf, benutzerId, benutzerName,
}: UploadArgs): Promise<ProjektDokument> => {
  const name = (dateiname || '').trim() || 'dokument.dat';
  const pfad = storagePfad(projectId, name, crypto.randomUUID());
  const typ = inhaltstyp(name, (datei as File).type);

  const { error: uploadFehler } = await supabase.storage
    .from(BUCKET)
    .upload(pfad, datei, { upsert: false, contentType: typ });
  if (uploadFehler) throw new Error(describeError(uploadFehler));

  const zeile = {
    project_id: projectId,
    kategorie: entwurf.kategorie,
    ordner: normalisiereOrdner(entwurf.ordner),
    titel: entwurf.titel.trim(),
    dateiname: name,
    storage_pfad: pfad,
    mime_typ: typ || null,
    groesse_bytes: (datei as File).size || 0,
    beschreibung: entwurf.beschreibung?.trim() || null,
    tags: entwurf.tags,
    module: entwurf.module,
    stand: entwurf.stand?.trim() || null,
    gueltig_bis: entwurf.gueltig_bis || null,
    // Ein Nicht-Admin, der hier `true` unterschiebt, laeuft in die
    // `with check`-Seite der Policy und bekommt eine Absage — die Oberflaeche
    // zeigt die Auswahl gar nicht erst.
    nur_admin: entwurf.nurAdmin === true,
    hochgeladen_von: benutzerId || null,
    hochgeladen_von_name: benutzerName || null,
    // Beide Kennzeichen NUR, wenn gesetzt: ein gewoehnlicher Upload schickt
    // die Schluessel gar nicht mit und laeuft damit auch auf einer Umgebung,
    // auf der Nr. 160/161 noch fehlt. Erst eine erzeugte Fassung braucht die
    // Spalten — und soll ohne sie sichtbar scheitern (42703/PGRST204), nicht
    // still als „hochgeladen" landen.
    ...(entwurf.erzeugt_aus ? { erzeugt_aus: entwurf.erzeugt_aus } : {}),
    ...(entwurf.fassung_ref?.trim() ? { fassung_ref: entwurf.fassung_ref.trim() } : {}),
  };

  const { data, error } = await supabase.from(TABELLE).insert(zeile).select().single();
  if (error) {
    // Rollback: die Datei waere sonst bezahlter Speicher ohne Verweis.
    //
    // Ein gescheiterter Rollback wird NICHT geworfen: der Anwender soll den
    // Grund des eigentlichen Fehlers sehen, nicht den der Aufraeumaktion.
    // Aber er wird auch nicht nur protokolliert: `rueckbau.verwaist` wandert
    // an den Fehler, damit ein Aufrufer, der den Zeilenfehler wegsteckt
    // (Doppelablage in `legeUnterlageAb`), die liegengebliebene Datei nicht
    // als Erfolg verkauft. `entferneDatei` prueft dabei die BESTAETIGUNG des
    // Storage und nicht nur das Fehlen eines Fehlers — ein von der Policy
    // abgewiesener Pfad kommt ohne `error` zurueck.
    const weg = await entferneDatei(pfad);
    if (!weg) console.warn('Verwaiste Datei in der Ablage:', pfad);
    // Mit Code: `legeUnterlageAb` erkennt daran den Wettlauf (23505) und die
    // fehlende Migration (42703). Text wie bisher.
    throw new AblageFehler(describeError(error), errorCode(error), { pfad, verwaist: !weg });
  }
  return mapZeile(data as Record<string, unknown>);
};

// ─── Ändern ─────────────────────────────────────────────────────────────────

/**
 * Spalten, die NIE aus einem Update-Objekt uebernommen werden.
 *
 * `id`/`project_id` sind die Identitaet der Zeile, `storage_pfad`/
 * `groesse_bytes`/`mime_typ`/`dateiname` beschreiben die abgelegte DATEI und
 * duerfen sich nur mit ihr aendern — und sie aendert sich nie (siehe (b)).
 * Ein Aufrufer, der arglos das ganze Dokument zurueckschreibt, haengte sonst
 * eine Zeile an eine fremde Datei.
 */
const GESPERRTE_SPALTEN = [
  'id', 'project_id', 'storage_pfad', 'groesse_bytes', 'mime_typ', 'dateiname',
  // Beide Felder des Hochladers, nicht nur die Id: der Anzeigename ist genau
  // deshalb denormalisiert, damit die Ablage auch nach dem Loeschen des
  // Profils noch sagen kann, WER die Datei abgelegt hat. Frei ueberschreibbar
  // leistete er das nicht mehr.
  'hochgeladen_von', 'hochgeladen_von_name',
  // Das Admin-Schloss hat seinen EIGENEN Weg (`setzeAdminSperre`) — dieselbe
  // Trennung wie bei den Buchungsfeldern der Einweisung. Ein Aufrufer, der
  // arglos das ganze Dokument zurueckschreibt, wuerde sonst eine Sperre
  // aufheben, ohne dass irgendwo ein Entsperren stattgefunden haette.
  'nur_admin',
  // Die Kennzeichnung einer erzeugten Fassung (Nr. 160/161) entsteht nur beim
  // Anlegen. Ueber das Bearbeiten liesse sich sonst eine Fassung zur
  // „hochgeladenen Datei" umschreiben — und damit ihr Loeschschutz und die
  // Ausnahme beim Archivieren aushebeln; umgekehrt wuerde ein Upload zur
  // Fassung, die nie eine war.
  'erzeugt_aus', 'fassung_ref',
  'created_at', 'updated_at',
] as const;

const ohneGesperrteSpalten = (updates: Partial<ProjektDokument>): Record<string, unknown> => {
  const rein: Record<string, unknown> = { ...updates };
  for (const spalte of GESPERRTE_SPALTEN) delete rein[spalte];
  if (typeof rein.ordner === 'string') rein.ordner = normalisiereOrdner(rein.ordner);
  if (typeof rein.titel === 'string') rein.titel = rein.titel.trim();
  // Ein geleertes Ablaufdatum muss als NULL ankommen. Ein ausgelassener
  // Schluessel loescht in PostgREST nichts — die Falle aus den Mindeststunden.
  if (rein.gueltig_bis === '') rein.gueltig_bis = null;
  if (rein.stand === '') rein.stand = null;
  if (rein.beschreibung === '') rein.beschreibung = null;
  return rein;
};

/** Metadaten eines Dokuments aendern (Titel, Kategorie, Ordner, Tags, Module, Stand, Ablauf). */
export const updateDokument = async (
  id: string,
  updates: Partial<ProjektDokument>,
): Promise<ProjektDokument> => {
  // Das Admin-Schloss ueber diesen Weg zu setzen ist ein Programmierfehler und
  // keine harmlose Auslassung: der Aufrufer glaubt zu sperren bzw. zu
  // entsperren, und die Zeile darunter wuerde ihm die unveraenderte Zeile als
  // Erfolg zurueckgeben. Lieber laut scheitern.
  if ('nur_admin' in updates) {
    throw new Error(
      'Das Admin-Schloss lässt sich nicht über updateDokument setzen — dafür gibt es setzeAdminSperre.',
    );
  }
  const rein = ohneGesperrteSpalten(updates);
  // Ein Aufrufer, der ausschliesslich gesperrte Felder geschickt hat, haette
  // hier ein leeres Update — PostgREST quittiert das mit 400 und `.single()`
  // wirft. Eine Aenderung, die nichts aendert, ist keine Fehlersituation.
  if (!Object.keys(rein).length) {
    const { data, error } = await supabase.from(TABELLE).select('*').eq('id', id).single();
    if (error) throw new Error(describeError(error));
    return mapZeile(data as Record<string, unknown>);
  }
  if (typeof rein.titel === 'string' && !rein.titel) {
    throw new Error('Das Dokument braucht einen Titel.');
  }
  const { data, error } = await supabase
    .from(TABELLE)
    .update(rein)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(describeError(error));
  return mapZeile(data as Record<string, unknown>);
};

/**
 * Das Admin-Schloss setzen oder aufheben.
 *
 * Eigener Weg statt eines Feldes im allgemeinen Update — dieselbe Ueberlegung
 * wie bei `markiereGebucht` in sigeEinweisungService: was besondere Rechte
 * braucht, bekommt eine besondere Tuer, damit es nicht versehentlich im
 * Ruecken einer anderen Aenderung mitgeschrieben wird.
 *
 * ═══ HIER STEHT KEINE ROLLENPRUEFUNG, UND DAS IST ABSICHT ════════════════
 * Der Riegel ist die Policy (Migration Nr. 38): `with check` verhindert das
 * SPERREN durch Nicht-Admins, `using` das ENTSPERREN (eine gesperrte Zeile
 * sehen sie gar nicht). Eine zusaetzliche Abfrage im Client waere eine zweite
 * Wahrheit ueber dieselbe Frage — und die, die sich umgehen laesst.
 *
 * Was der Client stattdessen tut: eine Absage verstaendlich machen. Und dabei
 * die ZWEI Absagen auseinanderhalten, die hier voellig verschieden aussehen:
 *
 *   • `with check` abgelehnt (SPERREN durch einen Nicht-Admin) → ein echter
 *     Fehler mit `42501` („new row violates row-level security policy").
 *   • `using` abgelehnt (die Zeile ist fuer den Aufrufer nicht sichtbar) →
 *     KEIN Fehler, sondern eine leere Ergebnismenge.
 *
 * Beides in einen Topf zu werfen war der erste Entwurf und war falsch: die
 * Meldung „nur die Verwaltung darf das Admin-Schloss setzen" waere dann
 * ausgerechnet im Sperr-Fall nie erschienen, dafuer aber bei einem geloeschten
 * Dokument oder verlorenem Projektzugriff — wo sie in die Irre fuehrt.
 */
/** 42501 = insufficient_privilege, hier: `with check` der Policy hat abgelehnt. */
const istPolicyVerstoss = (e: unknown): boolean => errorCode(e) === '42501';

export const setzeAdminSperre = async (
  id: string,
  gesperrt: boolean,
): Promise<ProjektDokument> => {
  const { data, error } = await supabase
    .from(TABELLE)
    .update({ nur_admin: gesperrt })
    .eq('id', id)
    .select()
    .maybeSingle();
  if (error) {
    if (istPolicyVerstoss(error)) {
      throw new Error(
        'Das Admin-Schloss lässt sich nur von der Verwaltung setzen (Rolle „admin" oder „super_admin").',
      );
    }
    throw new Error(describeError(error));
  }
  if (!data) {
    // Die Zeile war fuer den Aufrufer nicht sichtbar. Beim ENTSPERREN ist das
    // regelmaessig die fehlende Berechtigung (eine gesperrte Zeile sieht nur
    // die Verwaltung); sonst ist das Dokument weg oder der Projektzugriff
    // verloren. Die Meldung sagt beides, statt sich auf eine Vermutung
    // festzulegen.
    throw new Error(
      gesperrt
        ? 'Das Dokument ist nicht mehr vorhanden, oder Sie haben keinen Zugriff mehr auf dieses Projekt.'
        : 'Das Dokument ist nicht mehr vorhanden, oder es lässt sich nur von der Verwaltung entsperren.',
    );
  }
  return mapZeile(data as Record<string, unknown>);
};

// ─── Löschen ────────────────────────────────────────────────────────────────

/**
 * Loescht Dateien und meldet, welche danach noch da sind.
 *
 * WARUM diese Nachkontrolle noetig ist: `storage.remove()` liefert die Liste
 * der TATSAECHLICH entfernten Objekte zurueck. Ein Pfad, den die Policy
 * abweist, fehlt darin einfach — ohne `error`. Ein blosser Blick auf `error`
 * meldete also Erfolg, waehrend die Datei liegen bleibt, und genau das
 * unterlaeuft die Zusage dieses Moduls, dass „geloescht" und „Speicher frei"
 * dasselbe heissen.
 *
 * Ein FEHLENDER Pfad gilt dabei als Erfolg: eine schon nicht mehr vorhandene
 * Datei ist gelöscht. Unterscheidbar sind die beiden Faelle in der Antwort
 * nicht — deshalb meldet diese Funktion nicht „abgewiesen", sondern nur „nicht
 * bestaetigt", und der Aufrufer entscheidet, was das wert ist.
 */
const entferneDateien = async (pfade: string[]): Promise<string[]> => {
  const unbestaetigt: string[] = [];
  for (let i = 0; i < pfade.length; i += LOESCH_STAPEL) {
    const stapel = pfade.slice(i, i + LOESCH_STAPEL);
    const { data, error } = await supabase.storage.from(BUCKET).remove(stapel);
    if (error) throw new Error(describeError(error));
    const entfernt = new Set(
      (Array.isArray(data) ? data : []).map((o: { name?: string }) => o?.name).filter(Boolean) as string[],
    );
    for (const pfad of stapel) if (!entfernt.has(pfad)) unbestaetigt.push(pfad);
  }
  return unbestaetigt;
};

/**
 * Eine einzelne Datei entfernen und ehrlich sagen, ob sie weg ist. Wirft nie.
 *
 * `true` nur bei BESTAETIGTER Entfernung (der Pfad steht in der Antwort des
 * Storage). `false` bei Fehler oder fehlender Bestaetigung — nach einem eben
 * erst gelungenen Upload heisst das: die Datei liegt noch da. Fuer den
 * Rueckbau in `uploadDokument` und den zweiten Anlauf in `legeUnterlageAb`.
 */
export const entferneDatei = async (pfad: string): Promise<boolean> => {
  const p = (pfad || '').trim();
  if (!p) return true;
  try {
    const unbestaetigt = await entferneDateien([p]);
    return unbestaetigt.length === 0;
  } catch (e) {
    console.warn('Datei ließ sich nicht entfernen:', p, e);
    return false;
  }
};

/** Was `loescheDokument` ueber den Loeschenden wissen muss. */
export interface LoeschRechte {
  /**
   * Darf der Aufrufer von der Anwendung abgelegte Fassungen loeschen —
   * Verwaltung (`admin`/`super_admin`) oder Uebungsprojekt
   * (`darfErzeugteLoeschen` in dokumenteLogic.ts)? Vorgabe `false`.
   */
  darfErzeugteLoeschen?: boolean;
}

/**
 * Ein Dokument loeschen — erst die Datei, dann die Zeile (siehe (a)).
 *
 * Der Storage-Fehler wird bewusst NICHT verschluckt: wenn die Datei nicht
 * weggeht, soll die Zeile stehen bleiben und der Anwender es noch einmal
 * versuchen koennen.
 *
 * Eine nicht bestaetigte Entfernung bricht hier dagegen NICHT ab: sie ist der
 * Normalfall beim zweiten Anlauf (die Datei ist dann schon weg) und wuerde die
 * Zeile sonst dauerhaft unloeschbar machen. Sie wird protokolliert.
 *
 * ═══ ERZEUGTE FASSUNGEN: ABSAGE VOR DER DATEI ════════════════════════════
 * Die Policy (Nr. 160/161) laesst Nicht-Admins eine erzeugte Zeile nicht
 * loeschen. Sie prueft aber erst im ZWEITEN Schritt — die Datei waere nach
 * Reihenfolge (a) dann schon weg, und es bliebe eine Zeile ohne Datei: ein
 * Nachweis, der sich nicht mehr oeffnen laesst. Deshalb hier die Vorabpruefung
 * mit demselben Klartext, den die Oberflaeche am Knopf zeigt. Sie ist KEINE
 * zweite Wahrheit ueber die Berechtigung — der Riegel bleibt die Policy —,
 * sondern schuetzt die Datei vor dem Riegel.
 */
export const loescheDokument = async (
  dokument: Pick<ProjektDokument, 'id' | 'storage_pfad' | 'kategorie' | 'ordner' | 'module' | 'tags' | 'erzeugt_aus'>,
  rechte: LoeschRechte = {},
): Promise<void> => {
  const sperre = erzeugteLoeschSperre(dokument, rechte.darfErzeugteLoeschen === true);
  if (sperre) throw new Error(sperre);
  const pfad = (dokument.storage_pfad || '').trim();
  if (pfad) {
    const unbestaetigt = await entferneDateien([pfad]);
    if (unbestaetigt.length) {
      console.warn('Entfernen der Datei nicht bestätigt (evtl. bereits gelöscht):', pfad);
    }
  }
  const { error } = await supabase.from(TABELLE).delete().eq('id', dokument.id);
  if (error) throw new Error(describeError(error));
};

export interface LoeschErgebnis {
  anzahl: number;
  bytes: number;
  /**
   * Wie viele erzeugte Unterlagen stehen geblieben sind (`erzeugteBehalten`).
   * Ohne die Option immer 0.
   */
  behalten: number;
}

export interface LoeschOptionen {
  /**
   * Von der Anwendung abgelegte eigene Unterlagen (`istErzeugteUnterlage`:
   * Fassungen aus Vorankuendigung, SiGe-Plan, A+S-Plan, UfsA,
   * Baustellenordnung, Handbuch, Einweisung …) stehen lassen. Vorgabe
   * `false`: wer ein Projekt endgueltig loescht (AdminDeletionRequests,
   * Uebungsprojekte), will alles weg — nur das Archivieren nimmt die
   * Nachweise aus.
   */
  erzeugteBehalten?: boolean;
}

/**
 * Die ganze Ablage eines Projekts raeumen — der Weg hinter der Archiv-Frage.
 *
 * Beruehrt AUSSCHLIESSLICH `project_documents` und den Bucket
 * `projekt-dokumente`. Begehungsberichte, die Versionen des SiGe-Plans, der
 * UfsA, der Vorankuendigung, die Einweisungsblaetter und Rechnungen liegen in
 * anderen Tabellen und anderen Buckets und werden von hier aus nicht
 * angefasst — das ist der Grund, warum diese Funktion ueberhaupt gefahrlos
 * angeboten werden kann.
 *
 * ═══ AUSNAHME: ERZEUGTE UNTERLAGEN IN DER ABLAGE ═════════════════════════
 * Die Module legen ihre Fassungen als Nachweis IN dieser Ablage ab
 * (`services/unterlagenAblage.ts`, Kategorie `eigene_unterlagen`, Spalte
 * `erzeugt_aus`). Mit `erzeugteBehalten` werden sie vor dem Stapeln aus der
 * Liste genommen — Datei UND Zeile bleiben — und als `behalten` gemeldet. Die
 * Policy (Nr. 160/161) liesse einen Nicht-Admin ihre Zeilen ohnehin nicht
 * loeschen; die Ausnahme hier sorgt dafuer, dass auch die DATEIEN stehen
 * bleiben und kein Admin sie versehentlich mitraeumt.
 *
 * ═══ WARUM ZEILENWEISE UND NICHT `delete().eq('project_id', …)` ═══════════
 * Geloescht werden die Dateien der GEHOLTEN Liste. Wuerden die Zeilen danach
 * pauschal ueber `project_id` abgeraeumt, fielen beide Mengen auseinander,
 * sobald zwischen Lesen und Loeschen jemand etwas hochlaedt: dessen Zeile
 * waere weg, seine Datei laege noch da — unsichtbar und dauerhaft bezahlt.
 * Deshalb wird JEDER Stapel erst entfernt und dann genau ueber die Ids dieses
 * Stapels aus der Tabelle genommen. Bricht es dazwischen ab, bleibt der Rest
 * vollstaendig (Zeile UND Datei) stehen und ein erneuter Lauf holt ihn nach.
 *
 * ═══ GEZAEHLT WIRD, WAS DIE DATENBANK BESTAETIGT ═════════════════════════
 * Nicht die Laenge des Stapels, sondern die zurueckgegebenen Ids. Der
 * Unterschied ist nicht theoretisch: sperrt ein Admin ein Dokument zwischen
 * dem Lesen und dem Loeschen, faellt dessen Zeile aus der Policy und der
 * Delete nimmt sie stillschweigend nicht mit — die Datei ist dann aber schon
 * weg. Diese Zeile wird nicht mitgezaehlt, und der Rueckstand wird
 * protokolliert. Die Zahl landet als dauerhafter Vermerk am Projekt
 * (`documentsArchiveDecision`); ein Vermerk, der raet, ist wertlos.
 */
export const loescheAlleProjektDokumente = async (
  projectId: string,
  optionen: LoeschOptionen = {},
): Promise<LoeschErgebnis> => {
  const alle = await fetchProjektDokumente(projectId);
  const dokumente = optionen.erzeugteBehalten === true
    ? alle.filter(d => !istErzeugteUnterlage(d))
    : alle;
  const behalten = alle.length - dokumente.length;
  if (!dokumente.length) return { anzahl: 0, bytes: 0, behalten };

  let anzahl = 0;
  let bytes = 0;

  for (let i = 0; i < dokumente.length; i += LOESCH_STAPEL) {
    const stapel = dokumente.slice(i, i + LOESCH_STAPEL);

    const unbestaetigt = await entferneDateien(stapel.map(d => d.storage_pfad).filter(Boolean));
    if (unbestaetigt.length) {
      // Kein Abbruch: eine bereits fehlende Datei ist der Normalfall beim
      // zweiten Anlauf. Aber es gehoert ins Protokoll — sonst waere eine
      // abgewiesene Loeschung von einer erledigten nicht zu unterscheiden.
      console.warn(
        `Entfernen von ${unbestaetigt.length} Datei(en) nicht bestätigt:`,
        unbestaetigt,
      );
    }

    const { data, error } = await supabase
      .from(TABELLE)
      .delete()
      .in('id', stapel.map(d => d.id))
      .select('id');
    if (error) throw new Error(describeError(error));

    const geloescht = new Set((data || []).map((z: { id: string }) => z.id));
    if (geloescht.size < stapel.length) {
      console.warn(
        `${stapel.length - geloescht.size} Zeile(n) wurden nicht gelöscht `
        + '(vermutlich zwischenzeitlich gesperrt) — ihre Dateien sind bereits entfernt.',
      );
    }
    anzahl += geloescht.size;
    bytes += stapel
      .filter(d => geloescht.has(d.id))
      .reduce((summe, d) => summe + (d.groesse_bytes || 0), 0);
  }

  return { anzahl, bytes, behalten };
};

// ─── Ansehen und Herunterladen ──────────────────────────────────────────────

/**
 * Signierte URL zum Ansehen/Herunterladen.
 *
 * Der Bucket ist privat — es gibt keine oeffentliche URL, und das ist Absicht:
 * in der Ablage liegen Vertraege und Gefaehrdungsbeurteilungen fremder Firmen.
 */
export const signedUrlFuerDokument = async (
  pfad: string,
  sekunden = SIGNED_URL_SEKUNDEN,
): Promise<string> => {
  const p = (pfad || '').trim();
  if (!p) throw new Error('Zu diesem Eintrag ist keine Datei hinterlegt.');
  const eimer = storageMitMethode('createSignedUrl') as unknown as {
    createSignedUrl: (p: string, s: number) => Promise<{ data?: { signedUrl?: string }; error?: unknown }>;
  };
  const { data, error } = await eimer.createSignedUrl(p, sekunden);
  if (error) throw new Error(await speicherFehlertext(error, 'ansehen'));
  if (!data?.signedUrl) {
    throw new Error('Die Datei konnte nicht geladen werden — bitte erneut hochladen.');
  }
  return data.signedUrl;
};

/**
 * Die Datei als Blob — fuer das Herunterladen unter dem ORIGINALNAMEN.
 *
 * Ueber die signierte URL kaeme die Datei unter ihrem UUID-Namen an; der sagt
 * niemandem etwas. Deshalb der Umweg ueber `download` und einen
 * Objekt-URL-Link im Aufrufer.
 */
export const ladeDokumentHerunter = async (pfad: string): Promise<Blob> => {
  const eimer = storageMitMethode('download') as unknown as {
    download: (p: string) => Promise<{ data?: Blob | null; error?: unknown }>;
  };
  const { data, error } = await eimer.download(pfad);
  if (error) throw new Error(await speicherFehlertext(error, 'herunterladen'));
  if (!data) throw new Error('Die Datei ist in der Ablage nicht mehr vorhanden.');
  return data;
};
